import * as yup from 'yup';
import {DateTime} from 'luxon';
import {
  AwardingBodyIdentifiers,
  DEFAULT_MAX_STRING_MSG,
  ExamType,
  transformDate
} from 'core';
import {GroupAwardExam, GroupAwards} from 'store/candidate/types';
import {IdType} from 'store/idType/idTypeAPI';
import {BasketItemRetake} from 'store/basket/basketAPI';

const MAX_IDENTIFICATION_DOC_NO_LENGTH = 20;

const IDENTITY_DOCUMENT_VALIDATION_SCHEMA = yup.object().shape({
  selectedIdentificationDocument: yup.number().nullable().required('Please select identification document.'),
  identificationDocNo: yup
    .string()
    .required('Identification Document Number is required')
    .max(MAX_IDENTIFICATION_DOC_NO_LENGTH, DEFAULT_MAX_STRING_MSG),
  identificationDocumentHasNoExpiryDate: yup.boolean(),
  identificationDocExpiryDate: yup
    .date()
    .nullable()
    .when('identificationDocumentHasNoExpiryDate', {
      is: (noExpiryDate: boolean) => !noExpiryDate,
      then: yup
        .date()
        .required('Expiry date is required.')
        .transform(transformDate)
        .typeError('Expiry date is required.')
        .min(DateTime.local().toJSDate(), 'Expiry date cannot be in the past.'),
    }),
  confirmationSelected: yup.boolean().isTrue('You have to confirm above details are according to the ID Document.'),
});

const getPreviousCenterNumberValidator = ({
  awardingBodyId, required
}: { awardingBodyId: number, required: boolean}) => {
  let previousCenterNumberValidator = yup.string();

  if (required){
    previousCenterNumberValidator = previousCenterNumberValidator.required('Previous center number is required.');
  }

  if (awardingBodyId === AwardingBodyIdentifiers.CIE) {
    previousCenterNumberValidator = previousCenterNumberValidator.matches(
      /^[A-Z0-9]{5}$/,
      'Invalid Centre number format. Please enter 5 uppercase alphanumeric characters'
    );
  }

  return previousCenterNumberValidator;
};

const getPreviousCandidateNumber = ({required}: { required: boolean }) => {
  let previousCandidateNumberValidator = yup.string();
  if (required)
    previousCandidateNumberValidator = previousCandidateNumberValidator.required('Previous candidate number is required.');

  return previousCandidateNumberValidator.matches(/^\d{4}$/, 'Previous candidate number must be four digit number');
};

const getRetakeOrCarryForwardValidation = () => {
  return yup.object().shape({
    type: yup.number(),
  });
};

const getGroupAwardsValidation = () => {
  return yup.object().shape({
    agreementConfirmed: yup.boolean().when('groupAwardExams', {
      is: (groupAwardExams: GroupAwardExam[]) => !!groupAwardExams.find((x) => x.isSelected),
      then: yup.boolean().isTrue('You have to confirm agreement.'),
    }),
  });
};

export const getValidation = (idTypes: IdType[] | undefined, awardingBodyId: number, proceedWithCashinRequired: boolean) => {
  return yup.object().shape({
    ...IDENTITY_DOCUMENT_VALIDATION_SCHEMA.fields,
    examRetake: yup.array().of(getRetakeOrCarryForwardValidation()),
    groupAwards: getGroupAwardsValidation(),
        proceedWithCashin: yup
            .bool()
            .when(['proceedWithCashinRequired'], {
                is: (val : boolean) => val || proceedWithCashinRequired,
                then: yup.bool().isTrue("Required")
            }),
    ...{
      identificationDocumentHasNoExpiryDate: yup.bool().when('selectedIdentificationDocument', {
        is: (val: number) => idTypes?.find((x) => Number(x.id) === val)?.mustHaveExpiryDate,
        then: yup.bool().isFalse('Selected Identification Document have to have expiry date.'),
      }),
    },
    previousCandidateNumber: yup
      .string()
      .when('groupAwards', {
        is: (groupAwards: GroupAwards) => !!groupAwards.groupAwardExams.find((x) => x.isSelected),
        then: getPreviousCandidateNumber({required: false}),
      })
      .when('examRetake', {
        is: (examRetake: BasketItemRetake[]) => examRetake.find((x) => x.type !== ExamType.Standard),
        then: getPreviousCandidateNumber({required: true}),
      }),
    previousCentreNumber: yup
      .string()
      .when('groupAwards', {
        is: (groupAwards: GroupAwards) => !!groupAwards.groupAwardExams.find((x) => x.isSelected),
        then: getPreviousCenterNumberValidator({required: false, awardingBodyId: awardingBodyId}),
      })
      .when('examRetake', {
        is: (examRetake: BasketItemRetake[]) => examRetake.find((x) => x.type !== ExamType.Standard),
        then: getPreviousCenterNumberValidator({required: true, awardingBodyId: awardingBodyId}),
      }),
  });
};
